<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { IonContent, IonFooter, IonTextarea, IonToggle } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useSafety, { IncidentType } from '@/modules/safety/composables/useSafety';
import useGeolocation from '@/modules/safety/composables/useGeolocation';
import useToast from '@/shared/composables/useToast';
import BackButton from '@/shared/components/BackButton.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { reportIncident, getSafetyConfig, isReporting } = useSafety();
const { config } = getSafetyConfig();
const { getCurrentPosition, isSupported: geoSupported } = useGeolocation();
const { presentToast } = useToast();

const type = (route.params.type as IncidentType) ?? 'general';
const description = ref('');
const shareLocation = ref(true);
const selectedQuickMessage = ref<string | null>(null);
const manualLocationReason = ref<'missing' | 'disabled' | null>(null);
const isPreparingReport = ref(false);
const isSubmitting = computed(() => isPreparingReport.value || isReporting.value);
const manualLocationHint = computed(() => {
	if (manualLocationReason.value === 'missing') {
		return t('safety.location_missing_comment_hint');
	}

	if (manualLocationReason.value === 'disabled') {
		return t('safety.location_disabled_comment_hint');
	}

	return '';
});
const descriptionPlaceholder = computed(() => (manualLocationReason.value ? t('safety.describe_location_placeholder') : t('safety.describe_placeholder')));

const quickMessages = computed<string[]>(() => {
	const list = config.value?.quick_messages?.[type];
	const messages = Array.isArray(list) ? list : typeof list === 'string' ? [list] : [];

	return messages
		.flatMap((message) => message.split(/\r\n|\r|\n/))
		.map((message) => message.trim())
		.filter(Boolean);
});

const pickQuickMessage = (msg: string) => {
	const previousQuickMessage = selectedQuickMessage.value;
	const currentDescription = description.value.trim();

	if (previousQuickMessage === msg) {
		selectedQuickMessage.value = null;
		if (currentDescription === msg) {
			description.value = '';
		}
		return;
	}

	selectedQuickMessage.value = msg;
	if (!currentDescription || currentDescription === previousQuickMessage) {
		description.value = msg;
	}
};

watch(shareLocation, (enabled) => {
	if (config.value?.gps_required) {
		return;
	}

	if (enabled) {
		manualLocationReason.value = null;
		return;
	}

	if (manualLocationReason.value !== 'missing') {
		manualLocationReason.value = 'disabled';
	}
});

const submit = async () => {
	if (isSubmitting.value) {
		return;
	}

	if (config.value?.enabled === false) {
		await presentToast('top', t('safety.disabled'), 5000, 'danger');
		router.replace({ name: 'dashboard' });
		return;
	}

	const requiresLocation = config.value?.gps_required === true;
	const shouldRequestLocation = shareLocation.value || requiresLocation;
	const currentDescription = description.value.trim();

	if (!requiresLocation && !shareLocation.value && !currentDescription) {
		manualLocationReason.value = 'disabled';
		return;
	}

	isPreparingReport.value = shouldRequestLocation;
	const coords = shouldRequestLocation ? await getCurrentPosition(4000).finally(() => (isPreparingReport.value = false)) : null;

	if (requiresLocation && !coords) {
		await presentToast('top', t('safety.location_required'), 5000, 'danger');
		return;
	}

	if (shareLocation.value && !coords) {
		manualLocationReason.value = 'missing';
		shareLocation.value = false;
		return;
	}

	const incident = await reportIncident({
		type,
		description: description.value.trim() || selectedQuickMessage.value || undefined,
		latitude: coords?.latitude,
		longitude: coords?.longitude,
		gps_accuracy: coords?.accuracy,
	});
	if (incident) {
		router.replace({ name: 'safety-my-report-detail', params: { id: incident.id } });
	}
};
</script>

<template>
	<ion-content>
		<TopBar class="px-[34px]">
			<template #left>
				<BackButton to="/safety" />
			</template>
			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
					{{ t(`safety.types.${type}`) }}
				</div>
			</template>
		</TopBar>

		<div class="px-[34px] pb-[120px] w-full max-w-[600px] mx-auto">
			<p class="font-sourcesanspro-regular text-white/70 text-sm2 mb-4">{{ t(`safety.type_hint.${type}`) }}</p>

			<div v-if="quickMessages.length" class="mb-4 flex flex-wrap gap-2">
				<button v-for="msg in quickMessages" :key="msg" class="rounded-full px-3 py-1.5 text-sm2 font-sourcesanspro-semibold border" :class="selectedQuickMessage === msg ? 'bg-white text-black border-white' : 'text-white border-white/40'" @click="pickQuickMessage(msg)">
					{{ msg }}
				</button>
			</div>

			<div class="rounded backdrop-blur-xl bg-white/10 p-3">
				<ion-textarea v-model="description" :placeholder="descriptionPlaceholder" :auto-grow="true" :rows="4" class="text-white font-sourcesanspro-regular" />
			</div>
			<p v-if="manualLocationHint" class="mt-2 text-xs2 text-red-200 font-sourcesanspro-regular">
				{{ manualLocationHint }}
			</p>

			<div v-if="geoSupported" class="mt-4 rounded backdrop-blur-xl bg-white/10 p-4 flex items-center gap-3">
				<div class="flex-1">
					<div class="font-sourcesanspro-semibold text-base2 text-white">{{ t('safety.share_location') }}</div>
					<div class="text-white/60 text-xs2 font-sourcesanspro-regular">{{ t('safety.share_location_hint') }}</div>
				</div>
				<ion-toggle v-model="shareLocation" :disabled="config?.gps_required" />
			</div>

			<button :disabled="isSubmitting" class="mt-6 w-full h-[60px] rounded-full bg-red-600 text-white font-sourcesanspro-semibold text-base2 uppercase active:scale-[0.98] transition-transform disabled:opacity-70" @click="submit">
				{{ isSubmitting ? t('safety.reporting') : t('safety.send_report') }}
			</button>
		</div>
	</ion-content>

	<ion-footer>
		<NavigationBar item-active="safety" />
	</ion-footer>
</template>
